import { DateTime } from 'luxon';

import { colors } from 'packages/common-frontend/colors';
import { dollarNumberWithCommas } from 'packages/common-frontend/utils';

/**
 * Shared display formatters for the Backup Servicing (BUS) customer portal
 * views (Deal Overview, Monthly Deliverables, …). Kept in one place so the
 * date / currency / status-colour rendering stays consistent across screens.
 */

/** JSDate scalar → epoch ms. Render as a medium date, or a dash when absent. */
export function formatJsDate(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  return DateTime.fromMillis(ms).toLocaleString(DateTime.DATE_MED);
}

/** A FullDateStr ("YYYY-MM-DD") as a medium date (e.g. "Mar 31, 2026"). */
export function formatFullDate(value: string | null | undefined): string {
  if (!value) return '—';
  return DateTime.fromISO(value).toLocaleString(DateTime.DATE_MED);
}

/** A FullDateStr as a compact "MMM yyyy" label (e.g. "Mar 2026"). */
export function formatPeriodShort(value: string): string {
  return DateTime.fromISO(value).toFormat('LLL yyyy');
}

/**
 * A reporting period as the cycle's display name (e.g. "March 31, 2026 Cycle").
 * Spelled-out month: this is a headline, not a table cell, and it is how the
 * business refers to a cycle.
 */
export function formatCycleName(value: string | null | undefined): string {
  if (!value) return 'Cycle';
  return `${DateTime.fromISO(value).toFormat('LLLL d, yyyy')} Cycle`;
}

/** A dollar amount, or a dash when absent. */
export function formatDollars(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return String(dollarNumberWithCommas(value));
}

/** Colour for a reconcile / check `status` string (pass | fail | other). */
export function statusColor(status: string): string {
  if (status === 'pass') return colors.green[400];
  if (status === 'fail') return colors.red[400];
  return colors.gray[500];
}

/**
 * The servicer's accept/reject decision on a cycle's reconcile result
 * (`approvalStatus`: pending | accepted | rejected), as the portal words it.
 *
 * `rejected` is a **terminal** outcome, not a slower `pending`: no certificate
 * will be issued for this cycle until a corrected tape is sent and the cycle is
 * re-reconciled. Rendering it as "pending" is why a returned cycle used to look
 * like one still in flight. The portal deliberately never shows the internal
 * approver or approval note (see `PortalBusServicingCycle` in the schema), so
 * these labels name the outcome only.
 */
export function approvalLabel(approvalStatus: string): string {
  if (approvalStatus === 'accepted') return 'Approved';
  if (approvalStatus === 'rejected') return 'Returned';
  return 'Awaiting review';
}

/** Colour for an `approvalStatus`, matching {@link approvalLabel}. */
export function approvalColor(approvalStatus: string): string {
  if (approvalStatus === 'accepted') return colors.green[400];
  if (approvalStatus === 'rejected') return colors.red[400];
  return colors.gray[500];
}

/**
 * A cycle's overall `cycleStatus` as a human label ("Awaiting approval" rather
 * than `awaiting_approval`). The enum is snake_case in the DB and reaches the
 * portal verbatim.
 */
export function cycleStatusLabel(cycleStatus: string): string {
  const spaced = cycleStatus.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Colour for a `cycleStatus`. Only the two terminal off-ramps (`rejected`,
 * `blocked`) are red — every other value is a stage the cycle is legitimately
 * passing through, and colouring those as warnings would make a healthy
 * in-flight cycle look broken.
 *
 * Returns a concrete colour rather than a Chakra `colorScheme` name on purpose:
 * this app registers no `Badge` variant styles, so `colorScheme` on a `Badge` is
 * inert (every scheme resolves to the same flat grey). Explicit colours are what
 * the rest of these formatters do, and they actually render.
 */
export function cycleStatusColor(cycleStatus: string): string {
  if (cycleStatus === 'complete') return colors.green[400];
  if (cycleStatus === 'rejected' || cycleStatus === 'blocked') {
    return colors.red[400];
  }
  return colors.gray[500];
}
